import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import ImageEditor from '@uppy/image-editor';
import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import '@uppy/image-editor/css/style.min.css';
import './ProductModal.css';
import { apiService } from '../services/api';
import { useParams } from 'react-router-dom';
const ProductModal = ({ isOpen, onClose, onSave, product = null, defaultCatalogId = '' }) => {
 const {catalogId} = useParams();
   const [formData, setFormData] = useState({
    catalogId: catalogId || defaultCatalogId,
    name: '',
    description: '',
    price: '',
    salePrice: '',
    salePriceStartDate: '',
    salePriceEndDate: '',
    currency: 'USD',
    category: '',
    sku: '',
    stock: '',
    status: 'active',
    imageEmoji: '📦',
    image_url: '',
    productUrl: '',
    brand: '',
    retailerId: ''
  });
  const [errors, setErrors] = useState({});
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [isImageEdited, setIsImageEdited] = useState(false);
  const uppyDashboardRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const uploadFnRef = useRef(null);
  const pendingBlobRef = useRef(null);

  const isEditMode = !!product;

  // Initialize Uppy instance (without plugins initially - they'll be added in useEffect)
  const uppy = useMemo(() => {
    const uppyInstance = new Uppy({
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: ['image/*'],
        maxFileSize: 5 * 1024 * 1024 // 5MB
      },
      autoProceed: false
    });

    

    // Image Editor event handlers
    uppyInstance.on('file-editor:start', (file) => {
      console.log('Image editor opened for file:', file.name);
      // You can show a loading indicator or notification here
    });

    uppyInstance.on('file-editor:complete', (updatedFile) => {
      console.log('Image editing completed:', updatedFile);
      setIsImageEdited(true);

      const file = uppyInstance.getFile(updatedFile.id);
      const blob = file?.data || updatedFile.data;
      const name = file?.name || updatedFile.name;
      if (blob) {
        // Revoke any previous preview object URL
        if (pendingBlobRef.current?.objectUrl) {
          URL.revokeObjectURL(pendingBlobRef.current.objectUrl);
        }
        pendingBlobRef.current = null;
        // Show temporary blob URL preview while uploading to server
        const objectUrl = URL.createObjectURL(blob);
        setUploadedImageUrl(objectUrl);
        // Upload edited file to server immediately
        if (uploadFnRef.current) {
          uploadFnRef.current(blob, name, objectUrl);
        }
      }
    });

    uppyInstance.on('file-editor:cancel', () => {
      // User cancelled editor — upload the original (un-edited) file
      if (pendingBlobRef.current) {
        const { blob, name, objectUrl } = pendingBlobRef.current;
        pendingBlobRef.current = null;
        if (uploadFnRef.current) {
          uploadFnRef.current(blob, name, objectUrl);
        }
      }
    });

    // Handle file addition — store blob and show preview; actual upload happens after editor closes
    uppyInstance.on('file-added', (file) => {
      console.log('File added:', file.name, 'Source:', file.source);
      if (file.data && file.source !== 'ImageEditor') {
        const objectUrl = URL.createObjectURL(file.data);
        pendingBlobRef.current = { blob: file.data, name: file.name, objectUrl };
        // Show temporary preview; formData.image_url stays empty until server upload completes
        setUploadedImageUrl(objectUrl);
        setFormData(prev => ({ ...prev, image_url: '' }));
      }
    });

    // Handle file removal
    uppyInstance.on('file-removed', () => {
      if (pendingBlobRef.current?.objectUrl) {
        URL.revokeObjectURL(pendingBlobRef.current.objectUrl);
      }
      pendingBlobRef.current = null;
      setUploadedImageUrl('');
      setFormData(prev => ({ ...prev, image_url: '' }));
    });

    return uppyInstance;
  }, []);

  // Upload an image blob to the server and populate formData.imageUrl with the resulting server URL.
  // previewObjectUrl is the temporary blob URL shown during upload — it is revoked after upload.
  const uploadToServer = useCallback(async (blob, name, previewObjectUrl) => {
    setIsUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, name);
      fd.append('type', 'PRODUCT_IMAGE');
      const response = await apiService.products.uploadImage(fd);
      const mediaUrl = response.data?.data?.mediaUrl || '';
      const serverBase = (process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api/v1').replace(/\/api\/v1\/?$/, '');
      const serverUrl = serverBase + mediaUrl;
      setUploadedImageUrl(serverUrl);
      setFormData(prev => ({ ...prev, image_url: serverUrl }));
    } catch (err) {
      console.error('Image upload failed:', err);
      setUploadedImageUrl('');
      setFormData(prev => ({ ...prev, image_url: '' }));
    } finally {
      if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl);
      setIsUploading(false);
    }
  }, []);

  // Keep uploadFnRef pointing at the latest uploadToServer so Uppy handlers (in useMemo) can call it
  useEffect(() => {
    uploadFnRef.current = uploadToServer;
  }, [uploadToServer]);

  // Initialize Dashboard and ImageEditor plugins when modal opens and ref is available
  useEffect(() => {
    if (!isOpen || !uppy) return;

    // Use setTimeout to ensure DOM element is mounted
    const timer = setTimeout(() => {
      if (uppyDashboardRef.current) {
        // Check if Dashboard is already added
        const existingDashboard = uppy.getPlugin('Dashboard');
        const existingImageEditor = uppy.getPlugin('ImageEditor');
        
        // Add ImageEditor plugin FIRST (must be added before Dashboard)
        if (!existingImageEditor) {
          uppy.use(ImageEditor, {
            id: 'ImageEditor',
            quality: 0.9, // Higher quality for product images
            cropperOptions: {
              viewMode: 1, // Restrict crop box within canvas
              aspectRatio: NaN, // Allow free aspect ratio (can be changed to specific ratios)
              autoCropArea: 0.8, // 80% of the image area
              responsive: true,
              restore: false,
              guides: true, // Show grid guides
              center: true,
              highlight: false,
              cropBoxMovable: true,
              cropBoxResizable: true,
              toggleDragModeOnDblclick: false,
              minCropBoxWidth: 50,
              minCropBoxHeight: 50,
              ready: function() {
                // Called when cropper is ready
                console.log('Image editor ready');
              }
            },
            actions: {
              revert: true, // Revert to original
              rotate: true, // Rotate 90 degrees
              granularRotate: true, // Fine rotation control
              flip: true, // Flip horizontal/vertical
              zoomIn: true,
              zoomOut: true,
              cropSquare: true, // 1:1 aspect ratio
              cropWidescreen: true, // 16:9 aspect ratio
              cropWidescreenVertical: true, // 9:16 aspect ratio
            }
          });
        }

        // Add Dashboard plugin AFTER ImageEditor (with auto-open image editor)
        if (!existingDashboard) {
          uppy.use(Dashboard, {
            inline: true,
            target: uppyDashboardRef.current,
            width: '100%',
            height: 300,
            showProgressDetails: true,
            note: 'Images only, 1 file max, 5MB max. Click to edit image.',
            proudlyDisplayPoweredByUppy: false,
            // Auto-open image editor when image is added
            autoOpenImageEditor: true
          });
        }
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      // Cleanup: Remove plugins when modal closes
      const dashboardPlugin = uppy.getPlugin('Dashboard');
      if (dashboardPlugin) {
        uppy.removePlugin(dashboardPlugin);
      }
      const imageEditorPlugin = uppy.getPlugin('ImageEditor');
      if (imageEditorPlugin) {
        uppy.removePlugin(imageEditorPlugin);
      }
    };
  }, [isOpen, uppy]);

  const categories = [
    'Electronics',
    'Fashion',
    'Food & Beverage',
    'Sports & Fitness',
    'Home & Garden',
    'Books',
    'Toys & Games',
    'Health & Beauty',
    'Other'
  ];

  const currencies = ['INR', 'EUR', 'GBP', 'USD', 'JPY', 'CAD', 'AUD'];

  const emojis = ['📦', '📱', '💻', '⌚', '🎧', '📷', '☕', '🍕', '🧘', '👕', '👟', '🎮', '📚', '💄', '🧴'];

  const generateSku = () => 'SKU-' + Math.random().toString(36).toUpperCase().substr(2, 8);
  const generateRetailerId = () => 'ret_' + Math.random().toString(36).substr(2, 10);

  useEffect(() => {
    if (product) {
      setFormData({
        catalogId: product.metaCatalogId ? product.metaCatalogId.toString() : '',
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        salePrice: product.salePrice || '',
        salePriceStartDate: product.salePriceStartDate
          ? new Date(product.salePriceStartDate).toISOString().slice(0, 10)
          : '',
        salePriceEndDate: product.salePriceEndDate
          ? new Date(product.salePriceEndDate).toISOString().slice(0, 10)
          : '',
        currency: product.currency || 'INR',
        category: product.category || '',
        sku: product.sku || '',
        stock: product.stock !== undefined ? product.stock : '',
        status: product.status || 'active',
        imageEmoji: product.imageEmoji || '📦',
        image_url: product.image_url || product.imageUrl || '',
        productUrl: product.productUrl || '',
        brand: product.brand || '',
        retailerId: product.retailerId || ''
      });
      setUploadedImageUrl(product.image_url || product.imageUrl || '');
      setIsImageEdited(false);
      pendingBlobRef.current = null;
    } else {
      setFormData({
        catalogId: catalogId || '',
        name: '',
        description: '',
        price: '',
        salePrice: '',
        salePriceStartDate: '',
        salePriceEndDate: '',
        currency: 'INR',
        category: '',
        sku: generateSku(),
        stock: '',
        status: 'active',
        imageEmoji: '📦',
        image_url: '',
        productUrl: '',
        brand: '',
        retailerId: generateRetailerId()
      });
      setUploadedImageUrl('');
      setIsImageEdited(false);
      pendingBlobRef.current = null;
    }
    setErrors({});
    // Reset Uppy when modal opens/closes
    if (isOpen && uppy) {
      uppy.cancelAll();
    }
  }, [product, isOpen, uppy]);

  // Cleanup Uppy on unmount
  useEffect(() => {
    return () => {
      if (uppy) {
        // Cancel any ongoing uploads
        uppy.cancelAll();
        // Remove specific plugins by name
        const dashboardPlugin = uppy.getPlugin('Dashboard');
        if (dashboardPlugin) {
          uppy.removePlugin(dashboardPlugin);
        }
        const imageEditorPlugin = uppy.getPlugin('ImageEditor');
        if (imageEditorPlugin) {
          uppy.removePlugin(imageEditorPlugin);
        }
      }
    };
  }, [uppy]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // When the user manually enters/clears an image URL, sync the preview state
    if (name === 'image_url') {
      setUploadedImageUrl(value);
    }
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.sku.trim()) {
      newErrors.sku = 'SKU is required';
    }

    if (formData.stock === '' || parseInt(formData.stock) < 0) {
      newErrors.stock = 'Valid stock quantity is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const productData = {
      ...formData,
      price: parseFloat(formData.price),
      salePrice: formData.salePrice ? parseFloat(formData.salePrice) : null,
      stock: parseInt(formData.stock) || 0,
      catalogId: formData.catalogId || catalogId || ''
    };
    onSave(productData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content product-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Product' : 'Add New Product'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <input type="hidden" name="catalogId" value={formData.catalogId} />

          <div className="form-group">
            <label htmlFor="name">Product Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter product name"
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sku">SKU *</label>
              <div className="input-with-action">
                <input
                  type="text"
                  id="sku"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  placeholder="PROD-001"
                  className={errors.sku ? 'error' : ''}
                />
                <button
                  type="button"
                  className="btn-generate"
                  onClick={() => setFormData(prev => ({ ...prev, sku: generateSku() }))}
                  title="Auto-generate SKU"
                >&#x21BA;</button>
              </div>
              {errors.sku && <span className="error-message">{errors.sku}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="retailerId">Retailer ID</label>
              <div className="input-with-action">
                <input
                  type="text"
                  id="retailerId"
                  name="retailerId"
                  value={formData.retailerId}
                  onChange={handleChange}
                  placeholder="1578l12"
                />
                <button
                  type="button"
                  className="btn-generate"
                  onClick={() => setFormData(prev => ({ ...prev, retailerId: generateRetailerId() }))}
                  title="Auto-generate Retailer ID"
                >&#x21BA;</button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter product description"
              rows="3"
              className={errors.description ? 'error' : ''}
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price">Price *</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                className={errors.price ? 'error' : ''}
              />
              {errors.price && <span className="error-message">{errors.price}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="currency">Currency</label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                className="form-select"
              >
                {currencies.map(currency => (
                  <option key={currency} value={currency}>{currency}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="salePrice">Sale Price</label>
              <input
                type="number"
                id="salePrice"
                name="salePrice"
                value={formData.salePrice}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="brand">Brand</label>
              <input
                type="text"
                id="brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                placeholder="Brand name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="salePriceStartDate">Sale Start Date</label>
              <input
                type="date"
                id="salePriceStartDate"
                name="salePriceStartDate"
                value={formData.salePriceStartDate}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="salePriceEndDate">Sale End Date</label>
              <input
                type="date"
                id="salePriceEndDate"
                name="salePriceEndDate"
                value={formData.salePriceEndDate}
                onChange={handleChange}
                min={formData.salePriceStartDate || undefined}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={errors.category ? 'error form-select' : 'form-select'}
              >
                <option value="">Select category</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              {errors.category && <span className="error-message">{errors.category}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="stock">Stock Quantity *</label>
              <input
                type="number"
                id="stock"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                placeholder="0"
                min="0"
                className={errors.stock ? 'error' : ''}
              />
              {errors.stock && <span className="error-message">{errors.stock}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-select"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="imageEmoji">Product Icon</label>
              <div className="emoji-selector">
                <input
                  type="text"
                  id="imageEmoji"
                  name="imageEmoji"
                  value={formData.imageEmoji}
                  onChange={handleChange}
                  maxLength="2"
                  className="emoji-input"
                  placeholder="📦"
                />
                <div className="emoji-preview">{formData.imageEmoji}</div>
              </div>
              <div className="emoji-suggestions">
                {emojis.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    className={`emoji-option ${formData.imageEmoji === emoji ? 'selected' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, imageEmoji: emoji }))}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="section-divider">
            <h3>Media & Links</h3>
          </div>

          <div className="form-group">
            <label htmlFor="imageUrl">Product Image</label>
            <div className="uppy-upload-container">
              {isOpen && <div ref={uppyDashboardRef}></div>}
            </div>
            {(formData.image_url || uploadedImageUrl) && (
              <div className={`image-preview ${isImageEdited ? 'has-edited' : ''}`}>
                <img 
                  src={uploadedImageUrl || formData.image_url} 
                  alt="Preview" 
                  onError={(e) => { e.target.style.display = 'none'; }} 
                />
                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={() => {
                    if (pendingBlobRef.current?.objectUrl) {
                      URL.revokeObjectURL(pendingBlobRef.current.objectUrl);
                    }
                    pendingBlobRef.current = null;
                    setUploadedImageUrl('');
                    setIsImageEdited(false);
                    setFormData(prev => ({ ...prev, image_url: '' }));
                    uppy.cancelAll();
                  }}
                >
                  Remove Image
                </button>
              </div>
            )}
            <div className="image-url-fallback">
              <label htmlFor="imageUrl_manual">Or enter image URL manually:</label>
              <input
                type="url"
                id="imageUrl_manual"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="productUrl">Product URL</label>
            <input
              type="url"
              id="productUrl"
              name="productUrl"
              value={formData.productUrl}
              onChange={handleChange}
              placeholder="https://example.com/product"
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={isUploading}>
              {isUploading ? 'Uploading image...' : isEditMode ? 'Update Product' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductModal;

